import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import PaiementStripe from '../components/PaiementStripe';
import { LangContext } from '../App';
import api from '../services/api';

const OR = 'var(--couleur-or)';

const CLE_PUBLIQUE = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = CLE_PUBLIQUE ? loadStripe(CLE_PUBLIQUE) : null;

const T = {
  fr: {
    title: 'Paiement de votre réservation',
    chargement: 'Préparation du paiement…',
    erreurInit: 'Impossible de préparer le paiement.',
    stripeManquant:
      "La clé publique Stripe n'est pas configurée sur ce site (VITE_STRIPE_PUBLISHABLE_KEY).",
    recap: 'Récapitulatif',
    amount: 'Montant',
    commission: 'Commission ChasseHunt',
    total: 'Total à payer',
    securise: 'Paiement sécurisé via Stripe',
    retour: '← Retour à mon tableau de bord',
  },
  en: {
    title: 'Pay your reservation',
    chargement: 'Preparing payment…',
    erreurInit: 'Could not prepare payment.',
    stripeManquant:
      'The Stripe publishable key is not configured on this site (VITE_STRIPE_PUBLISHABLE_KEY).',
    recap: 'Summary',
    amount: 'Amount',
    commission: 'ChasseHunt fee',
    total: 'Total to pay',
    securise: 'Secure payment via Stripe',
    retour: '← Back to dashboard',
  },
};

function formatMontant(cents, lang) {
  if (cents == null) return '—';
  return new Intl.NumberFormat(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 2,
  }).format(Number(cents) / 100);
}

export default function Paiement() {
  const { miseId } = useParams();
  const { lang } = useContext(LangContext);
  const t = T[lang];

  const [donnees, setDonnees] = useState(null); // { client_secret, montant_total_cents, commission_cents, commission_percent }
  const [chargement, setCharge] = useState(true);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    if (!miseId) return;
    setCharge(true);
    api
      .post('/stripe/payment-intent', { bid_id: miseId })
      .then((r) => {
        setDonnees(r.data);
        setErreur(null);
      })
      .catch((e) => setErreur(e.response?.data?.error || t.erreurInit))
      .finally(() => setCharge(false));
  }, [miseId, t.erreurInit]);

  const returnUrl = `${window.location.origin}/paiement/succes?bid_id=${miseId}`;

  return (
    <PrivateLayout>
      <div style={{ maxWidth: 640, margin: '0 auto', paddingBottom: '4rem' }}>
        <PageHeader icon="💳" title={t.title} />

        {!CLE_PUBLIQUE && (
          <div
            style={{
              background: 'rgba(200,50,50,0.1)',
              border: '1px solid rgba(200,50,50,0.35)',
              borderRadius: '8px',
              padding: '1rem',
              color: 'var(--couleur-texte-erreur)',
              fontSize: '0.88rem',
              marginBottom: '1.5rem',
            }}
          >
            ⚠ {t.stripeManquant}
          </div>
        )}

        {chargement && (
          <div
            style={{
              background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '8px',
              padding: '1.4rem',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.4)',
            }}
          >
            {t.chargement}
          </div>
        )}

        {!chargement && erreur && (
          <div
            style={{
              background: 'rgba(200,50,50,0.1)',
              border: '1px solid rgba(200,50,50,0.35)',
              borderRadius: '8px',
              padding: '1rem',
              color: 'var(--couleur-texte-erreur)',
              fontSize: '0.9rem',
            }}
          >
            ⚠ {erreur}
          </div>
        )}

        {!chargement && !erreur && donnees && (
          <>
            <div
              style={{
                background: 'rgba(255,255,255,0.025)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '10px',
                padding: '1.2rem 1.4rem',
                marginBottom: '1.5rem',
              }}
            >
              <h2
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: 'rgba(255,255,255,0.45)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  margin: '0 0 0.9rem',
                }}
              >
                {t.recap}
              </h2>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.35rem 0',
                  fontSize: '0.92rem',
                }}
              >
                <span style={{ color: 'rgba(255,255,255,0.55)' }}>{t.amount}</span>
                <span style={{ color: 'var(--couleur-texte)' }}>
                  {formatMontant(donnees.montant_total_cents, lang)}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.35rem 0',
                  fontSize: '0.82rem',
                }}
              >
                <span style={{ color: 'rgba(255,255,255,0.4)' }}>
                  {t.commission} ({donnees.commission_percent}%)
                </span>
                <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                  {formatMontant(donnees.commission_cents, lang)}
                </span>
              </div>
              <div
                style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '0.6rem 0' }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.35rem 0',
                  fontSize: '1.05rem',
                  fontWeight: 700,
                }}
              >
                <span style={{ color: 'var(--couleur-texte)' }}>{t.total}</span>
                <span style={{ color: OR }}>
                  {formatMontant(donnees.montant_total_cents, lang)}
                </span>
              </div>
            </div>

            {stripePromise && donnees.client_secret && (
              <Elements
                stripe={stripePromise}
                options={{ clientSecret: donnees.client_secret, appearance: { theme: 'night' } }}
              >
                <PaiementStripe returnUrl={returnUrl} />
              </Elements>
            )}

            <div
              style={{
                marginTop: '1.2rem',
                textAlign: 'center',
                fontSize: '0.72rem',
                color: 'rgba(255,255,255,0.3)',
              }}
            >
              🔒 {t.securise}
            </div>
          </>
        )}
      </div>
    </PrivateLayout>
  );
}
